import React, { useState } from 'react';
import { fileAPI } from '../../utils/axios';
import { handleApiError, showConfirmDialog } from '../../utils/errorHandler';
import * as S from './FileCard.style';

const FileCard = ({ 
  file,
  onDataMapping,
  onMappingReset,
  onEditFile,
  onFileUpdate, // 파일 정보 업데이트 콜백
  onFileDelete  // 파일 삭제 완료 콜백
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [newFileName, setNewFileName] = useState(file.name || '');

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDataMapping = () => {
    console.log('[FILE_CARD]', 'Data mapping clicked for file:', file.id);
    onDataMapping?.(file.id);
  };

  const handleMappingReset = async () => {
    if (!showConfirmDialog('변수 매핑을 초기화하시겠습니까?')) {
      return;
    }

    setIsProcessing(true);
    try {
      console.log('[FILE_CARD]', 'Mapping reset for file:', file.id);
      // TODO: 매핑 초기화 API 구현 후 연동
      // await mappingAPI.resetMapping(file.id);
      
      alert('매핑이 초기화되었습니다.');
      onMappingReset?.(file.id);
      
    } catch (error) {
      console.error('[FILE_CARD]', 'Mapping reset error:', error);
      const errorInfo = handleApiError(error);
      alert(`매핑 초기화 실패: ${errorInfo.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleEditFile = () => {
    console.log('[FILE_CARD]', 'Edit file clicked for file:', file.id);
    onEditFile?.(file.id, file);
  };

  const handleDeleteFile = async () => {
    if (!showConfirmDialog(`파일 "${file.name}"을(를) 삭제하시겠습니까?`)) {
      return;
    }

    setIsProcessing(true);
    try {
      console.log('[FILE_CARD]', 'Deleting file:', file.id);
      
      const result = await fileAPI.delete(file.id);
      
      if (result.success) {
        console.log('[FILE_CARD]', 'File deleted successfully:', file.id);
        alert('파일이 삭제되었습니다.');
        onFileDelete?.(file.id);
      } else {
        throw new Error(result.message);
      }
      
    } catch (error) {
      console.error('[FILE_CARD]', 'File deletion error:', error);
      const errorInfo = handleApiError(error);
      
      if (errorInfo.status === 404) {
        alert('파일을 찾을 수 없습니다.');
      } else if (errorInfo.status === 403) {
        alert('파일 삭제 권한이 없습니다.');
      } else {
        alert(`파일 삭제 실패: ${errorInfo.message}`);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCopyFile = async () => {
    const newName = prompt('복사할 파일의 이름을 입력하세요:', `${file.name}_copy`);
    if (!newName || !newName.trim()) {
      return;
    }

    setIsProcessing(true);
    try {
      console.log('[FILE_CARD]', 'Copying file:', file.id, 'to:', newName.trim());
      
      const result = await fileAPI.copy(file.id, newName.trim());
      
      if (result.success) {
        console.log('[FILE_CARD]', 'File copied successfully:', result.data);
        alert(`파일이 "${result.data.filename}"로 복사되었습니다.`);
        
        // 부모 컴포넌트에 새 파일 정보 전달
        const copiedFile = {
          id: result.data.new_file_id,
          name: newName.trim(),
          fileName: result.data.filename,
          isActive: false,
          file_size: file.file_size,
          file_type: file.file_type,
          uploaded_at: new Date().toISOString()
        };
        
        onFileUpdate?.(copiedFile, 'copy');
        
      } else {
        throw new Error(result.message);
      }
      
    } catch (error) {
      console.error('[FILE_CARD]', 'File copy error:', error);
      const errorInfo = handleApiError(error);
      
      if (errorInfo.status === 404) {
        alert('원본 파일을 찾을 수 없습니다.');
      } else if (errorInfo.status === 403) {
        alert('파일 복사 권한이 없습니다.');
      } else {
        alert(`파일 복사 실패: ${errorInfo.message}`);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReuploadFile = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xlsx,.xls,.csv,.pdf,.doc,.docx';
    
    input.onchange = async (event) => {
      const newFile = event.target.files[0];
      if (!newFile) return;

      if (!showConfirmDialog(`기존 파일을 "${newFile.name}"로 교체하시겠습니까?`)) {
        return;
      }

      setIsProcessing(true);
      try {
        console.log('[FILE_CARD]', 'Reuploading file:', file.id, 'with:', newFile.name);
        
        const result = await fileAPI.reupload(file.id, newFile);
        
        if (result.success) {
          console.log('[FILE_CARD]', 'File reuploaded successfully:', result.data);
          alert('파일이 성공적으로 재업로드되었습니다.');
          
          // 파일 정보 업데이트
          const updatedFile = {
            ...file,
            fileName: result.data.filename,
            file_size: result.data.file_size,
            file_type: result.data.file_type,
            uploaded_at: new Date().toISOString()
          };
          
          onFileUpdate?.(updatedFile, 'reupload');
          
        } else {
          throw new Error(result.message);
        }
        
      } catch (error) {
        console.error('[FILE_CARD]', 'File reupload error:', error);
        const errorInfo = handleApiError(error);
        
        if (errorInfo.status === 404) {
          alert('원본 파일을 찾을 수 없습니다.');
        } else if (errorInfo.status === 403) {
          alert('파일 재업로드 권한이 없습니다.');
        } else {
          alert(`파일 재업로드 실패: ${errorInfo.message}`);
        }
      } finally {
        setIsProcessing(false);
      }
    };
    
    input.click();
  };

  const handleNameEdit = () => {
    setEditingName(true);
    setNewFileName(file.name || '');
  };

  const handleNameSave = () => {
    if (!newFileName.trim()) {
      alert('파일명을 입력해주세요.');
      return;
    }

    // TODO: 파일명 변경 API 구현 후 연동
    console.log('[FILE_CARD]', 'Saving new file name:', newFileName.trim());
    
    const updatedFile = {
      ...file,
      name: newFileName.trim()
    };
    
    onFileUpdate?.(updatedFile, 'rename');
    setEditingName(false);
    alert('파일명이 변경되었습니다.');
  };

  const handleNameCancel = () => {
    setNewFileName(file.name || '');
    setEditingName(false);
  };

  return (
    <S.FileCardContainer isProcessing={isProcessing}>
      {/* 처리 중 오버레이 */}
      {isProcessing && (
        <S.ProcessingOverlay>
          <S.ProcessingSpinner />
          <S.ProcessingText>처리 중...</S.ProcessingText>
        </S.ProcessingOverlay>
      )}

      <S.FileHeader>
        {editingName ? (
          <S.NameEditContainer>
            <S.NameInput
              type="text"
              value={newFileName}
              onChange={(e) => setNewFileName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') handleNameSave();
                if (e.key === 'Escape') handleNameCancel();
              }}
              maxLength={100}
              autoFocus
            />
            <S.NameEditButtons>
              <S.NameEditButton onClick={handleNameSave}>✓</S.NameEditButton>
              <S.NameEditButton onClick={handleNameCancel}>✕</S.NameEditButton>
            </S.NameEditButtons>
          </S.NameEditContainer>
        ) : (
          <>
            <S.DataTitle title={file.name}>{file.name}</S.DataTitle>
            {/* <S.EditIcon onClick={handleNameEdit} disabled={isProcessing} /> */}
          </>
        )}
      </S.FileHeader>
      
      <S.FileInfo>
        <S.FileName title={file.fileName}>{file.fileDescription}</S.FileName>
        <S.FileMetadata>
          {file.file_size && (
            <S.FileSize>크기: {formatFileSize(file.file_size)}</S.FileSize>
          )}
          {file.uploaded_at && (
            <S.UploadDate>업로드: {formatDate(file.uploaded_at)}</S.UploadDate>
          )}
        </S.FileMetadata>
      </S.FileInfo>

      <S.FileActions>
        <S.ActionIcon 
          type="reupload" 
          onClick={handleReuploadFile}
          disabled={isProcessing}
          title="파일 재업로드"
        />
        <S.ActionIcon 
          type="copy" 
          onClick={handleCopyFile}
          disabled={isProcessing}
          title="파일 복사"
        />
        <S.ActionIcon 
          type="delete" 
          onClick={handleDeleteFile}
          disabled={isProcessing}
          title="파일 삭제"
        />
      </S.FileActions>

      {/* <S.ButtonGroup>
        <S.ActionButton 
          variant="primary" 
          onClick={handleDataMapping}
          disabled={isProcessing}
        >
          데이터 매핑
        </S.ActionButton>
        <S.ActionButton 
          variant="secondary" 
          onClick={handleMappingReset}
          disabled={isProcessing}
        >
          매핑 초기화
        </S.ActionButton>
        <S.ActionButton 
          variant="edit" 
          onClick={handleEditFile}
          disabled={isProcessing}
        >
          파일 편집
        </S.ActionButton>
      </S.ButtonGroup> */}
    </S.FileCardContainer>
  );
};

export default FileCard;